import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Upload, Trash2, Copy, Check, FileImage, FileText } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function MediaLibrary() {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [copied, setCopied] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const fetchMedia = useCallback(async () => {
    try {
      const params = filter !== 'all' ? `?media_type=${filter}` : '';
      const res = await axios.get(`${API}/api/cms/media${params}`, { withCredentials: true });
      setMedia(res.data);
    } catch (err) {
      console.error('Failed to fetch media', err);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { fetchMedia(); }, [fetchMedia]);

  const handleUpload = async (files) => {
    if (!files.length) return;
    setUploading(true);
    for (const file of files) {
      const form = new FormData();
      form.append('file', file);
      try {
        await axios.post(`${API}/api/cms/media/upload`, form, { withCredentials: true });
      } catch (err) {
        console.error('Upload failed:', file.name, err);
      }
    }
    setUploading(false);
    fetchMedia();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this file?')) return;
    await axios.delete(`${API}/api/cms/media/${id}`, { withCredentials: true });
    fetchMedia();
  };

  const copyUrl = (item) => {
    const url = `${API}${item.file_url}`;
    navigator.clipboard.writeText(url);
    setCopied(item.id);
    setTimeout(() => setCopied(null), 2000);
  };

  const onDrop = (e) => { e.preventDefault(); setDragOver(false); handleUpload(Array.from(e.dataTransfer.files)); };
  const onDragOver = (e) => { e.preventDefault(); setDragOver(true); };
  const onDragLeave = () => setDragOver(false);

  const filters = [
    { key: 'all', label: 'All' },
    { key: 'image', label: 'Images' },
    { key: 'pdf', label: 'PDFs' },
    { key: 'document', label: 'Documents' },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAF8]" data-testid="media-library">
      <div className="max-w-6xl mx-auto px-6 py-16">
        <div className="mb-10">
          <Link to="/advisor/content" className="text-xs uppercase tracking-[0.15em] text-[#999] hover:text-[#1A1A1A] transition-colors">
            Content
          </Link>
          <h1 className="font-serif text-3xl text-[#1A1A1A] mt-4">Media Library</h1>
        </div>

        {/* Upload Zone */}
        <div
          onDrop={onDrop}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onClick={() => fileInputRef.current?.click()}
          className={`mb-10 border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-colors ${dragOver ? 'border-[#1A1A1A] bg-[#F0EDE6]' : 'border-[#E8E4DC] hover:border-[#999]'}`}
          data-testid="media-upload-zone"
        >
          <Upload size={24} className="mx-auto mb-3 text-[#999]" strokeWidth={1.5} />
          <p className="text-sm text-[#6B6B6B]">
            {uploading ? 'Uploading...' : 'Drag and drop files here, or click to browse'}
          </p>
          <p className="text-xs text-[#999] mt-1">Images (JPG, PNG, WebP, GIF) and documents (PDF, DOCX)</p>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,.pdf,.docx"
            className="hidden"
            onChange={(e) => handleUpload(Array.from(e.target.files))}
            data-testid="media-file-input"
          />
        </div>

        {/* Filters */}
        <div className="flex gap-2 mb-8">
          {filters.map(f => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${filter === f.key ? 'border-[#1A1A1A] text-[#1A1A1A] bg-white' : 'border-[#E8E4DC] text-[#6B6B6B] hover:border-[#999]'}`}
              data-testid={`media-filter-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Grid */}
        {loading ? (
          <p className="text-sm text-[#999]">Loading...</p>
        ) : media.length === 0 ? (
          <div className="text-center py-20">
            <FileImage size={32} className="mx-auto mb-4 text-[#E8E4DC]" strokeWidth={1} />
            <p className="text-sm text-[#999]">No media uploaded yet</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4" data-testid="media-grid">
            {media.map(item => (
              <div key={item.id} className="group relative bg-white border border-[#E8E4DC] rounded-lg overflow-hidden" data-testid={`media-item-${item.id}`}>
                {item.media_type === 'image' ? (
                  <img
                    src={`${API}${item.file_url}`}
                    alt={item.alt_text || item.original_filename}
                    className="w-full aspect-square object-cover"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full aspect-square flex items-center justify-center bg-[#F5F3EE]">
                    <FileText size={32} className="text-[#999]" strokeWidth={1} />
                  </div>
                )}
                <div className="p-2">
                  <p className="text-xs text-[#6B6B6B] truncate">{item.original_filename}</p>
                  <p className="text-[10px] text-[#999]">{(item.size_bytes / 1024).toFixed(0)} KB</p>
                </div>
                <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => copyUrl(item)}
                    className="p-1.5 bg-white/90 rounded shadow-sm hover:bg-white"
                    title="Copy URL"
                    data-testid={`media-copy-${item.id}`}
                  >
                    {copied === item.id ? <Check size={12} className="text-green-600" /> : <Copy size={12} className="text-[#6B6B6B]" />}
                  </button>
                  <button
                    onClick={() => handleDelete(item.id)}
                    className="p-1.5 bg-white/90 rounded shadow-sm hover:bg-white"
                    title="Delete"
                    data-testid={`media-delete-${item.id}`}
                  >
                    <Trash2 size={12} className="text-red-400" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
